/**
 * rice 后端的接口封装。
 *
 * 每个函数对应一个 REST 端点,不再走 `server.dao('POST /verb-noun')` 那套。
 * 迁移状态见 docs 里的 backend-migration-plan.md —— core 尚未迁移的接口
 * 仍然通过 `server.dao(...)` 调用,两者会共存一段时间。
 */
import {riceRequest} from './client'
import {
  type Announcement,
  type App,
  type Attachment,
  type Badge,
  type Banner,
  type FoundationSettings,
  type GrainTransfer,
  type Node,
  type Page,
  type Proposal,
  type ProposalComment,
  type ProposalRelation,
  type ProposalStatus,
  type PublicUser,
  type Session,
  type User,
  type VerificationChannel,
  type VerificationPurpose,
  type Vote,
  type VoteChoice,
} from './types'

export * from './types'
export {attachmentUrl, RiceError} from './client'

// ── 内容位(匿名可读)────────────────────────────────────────────────────

export function listApps(): Promise<App[]> {
  return riceRequest<App[]>('/api/apps')
}

export function listBanners(): Promise<Banner[]> {
  return riceRequest<Banner[]>('/api/banners')
}

export function listAnnouncements(params: {limit?: number; before?: string} = {}) {
  return riceRequest<Page<Announcement>>('/api/announcements', {query: params})
}

export function getAnnouncement(id: string): Promise<Announcement> {
  return riceRequest<Announcement>(`/api/announcements/${encodeURIComponent(id)}`)
}

export function getFoundationSettings(): Promise<FoundationSettings> {
  return riceRequest<FoundationSettings>('/api/settings/foundation')
}

// ── 注册 / 登录 ─────────────────────────────────────────────────────────

/** 发验证码。成功无响应体。 */
export function sendVerificationCode(params: {
  channel: VerificationChannel
  purpose: VerificationPurpose
  phone?: string
  phone_region?: string
  email?: string
}): Promise<void> {
  return riceRequest<void>('/api/verification_codes', {method: 'POST', body: params})
}

/**
 * 注册第一步:校验验证码换注册票。
 * 票里带着已验证的手机号/邮箱,后续注册不需要也不能再传一遍。
 */
export function verifyRegistration(params: {
  channel: VerificationChannel
  code: string
  phone?: string
  phone_region?: string
  email?: string
}): Promise<{ticket: string; expires_in: number}> {
  return riceRequest('/api/registrations/verification', {method: 'POST', body: params})
}

/** 注册第二步:凭票 + handle + 密码建账号。 */
export function register(params: {
  ticket: string
  handle: string
  password: string
}): Promise<Session> {
  return riceRequest<Session>('/api/registrations', {method: 'POST', body: params})
}

/** 登录。identifier 可以是 handle / 邮箱 / 手机号。 */
export function login(params: {identifier: string; password: string}): Promise<Session> {
  return riceRequest<Session>('/api/session', {method: 'POST', body: params})
}

export function logout(): Promise<void> {
  return riceRequest<void>('/api/session', {method: 'DELETE'})
}

/**
 * 凭验证码重置密码。匿名可用 —— 忘了密码的人本来就登不进来。
 * 成功后该用户的所有 rice 令牌都会失效。
 */
export function resetPassword(params: {
  channel: VerificationChannel
  code: string
  password: string
  phone?: string
  phone_region?: string
  email?: string
}): Promise<void> {
  return riceRequest<void>('/api/passwords/reset', {method: 'POST', body: params})
}

// ── 当前用户 ────────────────────────────────────────────────────────────

export function getCurrentUser(token?: string): Promise<User> {
  return riceRequest<User>('/api/users/me', {token})
}

export function updateProfile(params: {
  nickname?: string
  bio?: string
  avatar_id?: string | null
}): Promise<User> {
  return riceRequest<User>('/api/users/me', {method: 'PATCH', body: params})
}

/** 改绑手机。需要新号码上收到的验证码。 */
export function changePhone(params: {
  phone: string
  phone_region?: string
  code: string
}): Promise<User> {
  return riceRequest<User>('/api/users/me/phone', {method: 'PUT', body: params})
}

/** 改绑邮箱。 */
export function changeEmail(params: {email: string; code: string}): Promise<User> {
  return riceRequest<User>('/api/users/me/email', {method: 'PUT', body: params})
}

/** 注销。不可逆,所以和改绑一样要当场验一次联系方式。 */
export function deleteAccount(params: {
  channel: VerificationChannel
  code: string
}): Promise<void> {
  return riceRequest<void>('/api/users/me', {method: 'DELETE', body: params})
}

// ── 附件 ────────────────────────────────────────────────────────────────

/** 上传。需要登录 —— core 的 /file/upload 是匿名的,rice 不是。 */
export function uploadAttachment(file: File | Blob, kind: 'image' | 'file' = 'image') {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  return riceRequest<Attachment>('/api/attachments', {method: 'POST', body: form})
}

// ── 节点 ────────────────────────────────────────────────────────────────

export function listNodes(): Promise<Node[]> {
  return riceRequest<Node[]>('/api/nodes')
}

export function listNodeMembers(): Promise<PublicUser[]> {
  return riceRequest<PublicUser[]>('/api/nodes/members')
}

// ── 勋章 ────────────────────────────────────────────────────────────────

/**
 * 某个人的勋章墙:勋章**全集**,每一枚带上这个人的 `awarded_at`(没获得是 null)。
 * 墙上要把没获得的也灰着显示,所以不能只返回已获得的。
 *
 * `user` 传 handle / DID / rice id,或 `'me'` 表示当前登录用户。
 */
export function listBadges(user: string): Promise<Badge[]> {
  return riceRequest<Badge[]>(`/api/users/${encodeURIComponent(user)}/badges`)
}

// ── 稻米 ────────────────────────────────────────────────────────────────

/** 我的稻米明细。收和付都在里面,靠 `direction` 区分。 */
export function listGrainTransfers(params: {limit?: number; before?: string} = {}) {
  return riceRequest<Page<GrainTransfer>>('/api/grain_transfers', {query: params})
}

/** 后台发放记录,公开可读。 */
export function listGrainGrants(params: {limit?: number; before?: string} = {}) {
  return riceRequest<Page<GrainTransfer>>('/api/grain_grants', {query: params})
}

/**
 * 转稻米。`to` 是对方的 DID 或 rice id。
 * 付款方永远是当前登录用户 —— 服务端不接受任何指定付款方的参数。
 */
export function createGrainTransfer(params: {
  to: string
  amount: number
  kind?: 'reward' | 'gift'
  memo?: string
  subject_uri?: string
}): Promise<GrainTransfer> {
  return riceRequest<GrainTransfer>('/api/grain_transfers', {method: 'POST', body: params})
}

// ── 提案 ────────────────────────────────────────────────────────────────

/**
 * 提案列表。`mine` 传了就是「与我相关」的那一份(需要登录),
 * 不传就是公开列表。
 */
export function listProposals(
  params: {
    status?: ProposalStatus
    mine?: ProposalRelation
    limit?: number
    before?: string
  } = {},
) {
  return riceRequest<Page<Proposal>>('/api/proposals', {query: params})
}

export function getProposal(id: string): Promise<Proposal> {
  return riceRequest<Proposal>(`/api/proposals/${encodeURIComponent(id)}`)
}

export function createProposal(params: {
  title: string
  closes_at: string
  attachment_id?: string
}): Promise<Proposal> {
  return riceRequest<Proposal>('/api/proposals', {method: 'POST', body: params})
}

export function deleteProposal(id: string): Promise<void> {
  return riceRequest<void>(`/api/proposals/${encodeURIComponent(id)}`, {method: 'DELETE'})
}

/** 我在这个提案上投了什么。没投过是 null。 */
export function getMyVote(proposalId: string): Promise<Vote | null> {
  return riceRequest<Vote | null>(`/api/proposals/${encodeURIComponent(proposalId)}/vote`)
}

export function vote(proposalId: string, choice: VoteChoice): Promise<Vote> {
  return riceRequest<Vote>(`/api/proposals/${encodeURIComponent(proposalId)}/vote`, {
    method: 'POST',
    body: {choice},
  })
}

export function listComments(
  proposalId: string,
  params: {limit?: number; before?: string} = {},
) {
  return riceRequest<Page<ProposalComment>>(
    `/api/proposals/${encodeURIComponent(proposalId)}/comments`,
    {query: params},
  )
}

export function createComment(proposalId: string, body: string): Promise<ProposalComment> {
  return riceRequest<ProposalComment>(
    `/api/proposals/${encodeURIComponent(proposalId)}/comments`,
    {method: 'POST', body: {body}},
  )
}

export function deleteComment(proposalId: string, commentId: string): Promise<void> {
  return riceRequest<void>(
    `/api/proposals/${encodeURIComponent(proposalId)}/comments/${encodeURIComponent(commentId)}`,
    {method: 'DELETE'},
  )
}
